import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject
} from 'react';
import {
  ReactFlow,
  MiniMap,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Panel,
  type Edge,
  type Node,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { useAppContext } from '@/Main';

import {
  useAddClusteringNodeMutation,
  useDeleteClusteringNodeMutation,
  useUpdateClusteringNodeMutation
} from '@/app/KeywordResearch/api/clusteringApi';
import { successMessage, errorMessage, warningMessage } from '@/utils/toast';
import CancelModal from '@/app/common/components/CancelModal';
import ClusterNode from './ClusterNode';
import ClusterToolbar from './ClusterToolbar';
import ClusterCanvasControls from './ClusterCanvasControls';
import ClusterDetails from './ClusterDetails';
import ClusterBreadcrumb from './ClusterBreadcrumb';
import ClusterStats from './ClusterStats';
import ClusterBadgeLegend from './ClusterBadgeLegend';
import ExportPreviewModal from './ExportPreviewModal';
import CompareModal from './CompareModal';
import ClusterNodeEditorModal, {
  type NodeEditorMode
} from './ClusterNodeEditorModal';
import ClusterNodeContextMenu, {
  type ClusterNodeContextMenuState
} from './ClusterNodeContextMenu';
import { layoutVisibleTree } from '../utils/treeToFlow';
import {
  buildTreeModel,
  collectExpandableIds,
  defaultExpandedIds,
  expandPathsToMatches,
  findMatchingIds,
  getAncestorIds,
  getDescendantIds,
} from '../utils/treeModel';
import {
  addChildNode,
  deleteNodeAtPath,
  getRawPathFromModel,
  hierarchicalNodeFromApi,
  toApiNamePath,
  updateNodeAtPath,
  type NewNodePayload,
  type NodePatch
} from '../utils/treeMutations';
import {
  copyKeywords,
  copySelectedPath,
  exportDecodedJson,
  exportKeywordsTxt,
  exportPathsCsv,
  getExportBaseName,
} from '../utils/exportCluster';
import {
  captureFlowPng,
  exportFlowJpeg,
  exportFlowPng,
  exportFlowSvg,
} from '../utils/exportImage';
import { computeTreeStats } from '../utils/treeStats';
import { readUrlState, writeUrlState } from '../utils/urlState';
import type {
  ClusterData,
  ClusterModeId,
  ClusterNodeData,
  HierarchicalNode,
  SelectedClusterNode,
  TreeNode,
  VolumeThresholds
} from '../types';

const nodeTypes: NodeTypes = { cluster: ClusterNode };

type FlowNode = Node<ClusterNodeData>;
type DecorateGraphArgs = {
  nodes: FlowNode[];
  edges: Edge[];
  matchIds: Set<string>;
  selectedId: string | null;
  pathIds: Set<string>;
  pinnedIds: Set<string>;
  query: string;
  onToggle: (id: string) => void;
  compact: boolean;
  showBadges: boolean;
  locked: boolean;
  canEdit: boolean;
  onAddChild?: (id: string) => void;
  onDelete?: (id: string) => void;
};

type PreviewState = {
  show: boolean;
  loading: boolean;
  url: string;
  filename: string;
};

type ClusterFlowCanvasProps = {
  hierarchical: HierarchicalNode | null;
  clusterData?: ClusterData | null;
  clusterMode?: ClusterModeId | string;
  onClusterModeChange?: (mode: ClusterModeId | string) => void;
  clusterSummary?: string | null;
  thresholds?: VolumeThresholds | null;
  /** Persist local tree edits (source hierarchical, not reclustered view). */
  onHierarchicalChange?: (next: HierarchicalNode) => void;
  /** When false, add/edit/delete UI is hidden (e.g. recluster mode). */
  canEditNodes?: boolean;
  /** Page shell that must stay inside fullscreen (view tabs + chart). */
  fullscreenRootRef?: RefObject<HTMLDivElement | null>;
};

type ClusterFlowProps = ClusterFlowCanvasProps;

function decorateGraph({
  nodes,
  edges,
  matchIds,
  selectedId,
  pathIds,
  pinnedIds,
  query,
  onToggle,
  compact,
  showBadges,
  locked,
  canEdit,
  onAddChild,
  onDelete
}: DecorateGraphArgs): { nodes: FlowNode[]; edges: Edge[] } {
  const hasQuery = Boolean(query.trim());

  const nextNodes = nodes.map((node) => {
    const isMatch = matchIds.has(node.id);
    const isOnPath = pathIds.has(node.id);
    const isSelected = node.id === selectedId;
    const isPinned = pinnedIds.has(node.id);
    const isDimmed = hasQuery && !isMatch && !isOnPath && !isPinned;

    return {
      ...node,
      selected: isSelected,
      draggable: !locked,
      data: {
        ...node.data,
        id: node.id,
        isMatch,
        isOnPath: isOnPath && !isSelected,
        isDimmed,
        isPinned,
        compact,
        showBadges,
        locked,
        canEdit,
        onToggle: locked ? undefined : onToggle,
        onAddChild: canEdit ? onAddChild : undefined,
        onDelete: canEdit ? onDelete : undefined,
      },
    };
  });

  const nextEdges = edges.map((edge) => {
    const onPath = pathIds.has(edge.source) && pathIds.has(edge.target);
    return {
      ...edge,
      animated: onPath,
      className: onPath ? 'cluster-edge cluster-edge--path' : 'cluster-edge',
      style: {
        ...edge.style,
        stroke: onPath
          ? 'var(--falcon-primary)'
          : 'var(--falcon-gray-600)',
        strokeWidth: onPath ? 2.4 : 1.4,
        opacity: hasQuery && !onPath ? 0.22 : 1,
      },
    };
  });

  return { nodes: nextNodes, edges: nextEdges };
}

function ClusterFlowCanvas({
  hierarchical,
  clusterData,
  clusterMode = 'default',
  onClusterModeChange,
  clusterSummary = '',
  thresholds = null,
  onHierarchicalChange,
  canEditNodes = false,
  fullscreenRootRef
}: ClusterFlowCanvasProps) {
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const { fitView, setCenter, getNode, getNodes, zoomIn, zoomOut } = useReactFlow();
  const {
    config: { isDark }
  } = useAppContext();
  const model = useMemo(() => buildTreeModel(hierarchical), [hierarchical]);
  const stats = useMemo(() => computeTreeStats(model.nodesById), [model.nodesById]);
  const initialUrl = useMemo(() => readUrlState(), []);

  const [expandedIds, setExpandedIds] = useState(() =>
    defaultExpandedIds(model.nodesById, 1),
  );
  const [undoStack, setUndoStack] = useState<string[][]>([]);
  const [query, setQuery] = useState(initialUrl.q);
  const [selectedId, setSelectedId] = useState(initialUrl.sel || null);
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => new Set());
  const [depthFilter, setDepthFilter] = useState(initialUrl.depth);
  const [compact, setCompact] = useState(initialUrl.compact);
  const [showBadges, setShowBadges] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [layoutKey, setLayoutKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [preview, setPreview] = useState<PreviewState>({
    show: false,
    loading: false,
    url: '',
    filename: '',
  });
  const [compareOpen, setCompareOpen] = useState(false);
  const [presentation, setPresentation] = useState(false);
  const [presentationStep, setPresentationStep] = useState(0);
  const [locked, setLocked] = useState(false);
  const [showMiniMap, setShowMiniMap] = useState(false);
  const miniMapHideTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const [contextMenu, setContextMenu] =
    useState<ClusterNodeContextMenuState>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<NodeEditorMode>('add');
  const [editorTargetId, setEditorTargetId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const [addNode, { isLoading: isAdding }] = useAddClusteringNodeMutation();
  const [updateNode, { isLoading: isUpdating }] =
    useUpdateClusteringNodeMutation();
  const [deleteNode, { isLoading: isDeleting }] =
    useDeleteClusteringNodeMutation();
  const editBusy = isAdding || isUpdating || isDeleting;
  const taskId = clusterData?.task_id ? String(clusterData.task_id) : '';
  const nodesEditable = Boolean(
    canEditNodes && onHierarchicalChange && hierarchical && taskId
  );

  // When reclustering mode changes, reset tree interaction state.
  useEffect(() => {
    setExpandedIds(defaultExpandedIds(model.nodesById, 1));
    setUndoStack([]);
    setSelectedId(null);
    setPinnedIds(new Set());
    setDepthFilter(null);
    setLayoutKey((key) => key + 1);
    window.setTimeout(() => fitView({ padding: 0.18, duration: 280 }), 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterMode, hierarchical]);

  const pushUndo = useCallback((current: Set<string>) => {
    setUndoStack((stack) => [...stack.slice(-19), [...current]]);
  }, []);

  const commitExpanded = useCallback(
    (updater: Set<string> | ((previous: Set<string>) => Set<string>)) => {
      setExpandedIds((prev) => {
        pushUndo(prev);
        return typeof updater === 'function' ? updater(prev) : updater;
      });
    },
    [pushUndo],
  );

  const matchIds = useMemo(
    () => findMatchingIds(model.nodesById, query),
    [model.nodesById, query],
  );

  const pathIds = useMemo<Set<string>>(() => {
    if (!selectedId) return new Set<string>();
    return new Set([selectedId, ...getAncestorIds(model.nodesById, selectedId)]);
  }, [model.nodesById, selectedId]);

  const onToggle = useCallback(
    (nodeId: string) => {
      if (!nodeId || locked) return;
      commitExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(nodeId)) next.delete(nodeId);
        else next.add(nodeId);
        return next;
      });
    },
    [commitExpanded, locked],
  );

  const showStatus = useCallback((message: string) => {
    setStatusMessage(message);
    window.setTimeout(() => setStatusMessage(''), 2800);
  }, []);

  const ensureEditable = useCallback(() => {
    if (!nodesEditable) {
      warningMessage(
        clusterMode !== 'default'
          ? 'برای ویرایش درخت، حالت خوشه‌بندی را روی پیش‌فرض بگذارید'
          : 'ویرایش نود در این حالت فعال نیست'
      );
      return false;
    }
    if (locked) {
      showStatus('در حالت قفل امکان ویرایش نیست');
      return false;
    }
    return true;
  }, [clusterMode, locked, nodesEditable, showStatus]);

  const openAddEditor = useCallback(
    (parentId: string) => {
      if (!ensureEditable()) return;
      setSelectedId(parentId);
      setEditorMode('add');
      setEditorTargetId(parentId);
      setEditorOpen(true);
    },
    [ensureEditable]
  );

  const requestDeleteNode = useCallback(
    (nodeId: string) => {
      if (!ensureEditable()) return;
      const treeNode = model.nodesById.get(nodeId);
      if (!treeNode) return;
      if (treeNode.depth === 0) {
        warningMessage('نود ریشه قابل حذف نیست');
        return;
      }
      setPendingDeleteId(nodeId);
      setShowDeleteModal(true);
    },
    [ensureEditable, model.nodesById]
  );

  const baseGraph = useMemo(
    () =>
      layoutVisibleTree(model, expandedIds, {
        compact,
        showBadges,
        maxDepth: depthFilter,
      }),
    [model, expandedIds, compact, showBadges, depthFilter, layoutKey],
  );

  const decorated = useMemo(
    () =>
      decorateGraph({
        ...baseGraph,
        matchIds,
        selectedId,
        pathIds,
        pinnedIds,
        query,
        onToggle,
        compact,
        showBadges,
        locked,
        canEdit: nodesEditable && !locked,
        onAddChild: openAddEditor,
        onDelete: requestDeleteNode,
      }),
    [
      baseGraph,
      matchIds,
      selectedId,
      pathIds,
      pinnedIds,
      query,
      onToggle,
      compact,
      showBadges,
      locked,
      nodesEditable,
      openAddEditor,
      requestDeleteNode
    ],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(decorated.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(decorated.edges);

  useEffect(() => {
    setNodes(decorated.nodes);
    setEdges(decorated.edges);
  }, [decorated, setNodes, setEdges]);

  useEffect(() => {
    writeUrlState({
      q: query,
      sel: selectedId ?? '',
      depth: depthFilter,
      compact,
      tab: 'tree',
      clusterBy: clusterMode,
    });
  }, [query, selectedId, depthFilter, compact, clusterMode]);

  const focusNode = useCallback(
    (id: string) => {
      const node = getNode(id) || decorated.nodes.find((item) => item.id === id);
      if (!node) return;
      const width = node.measured?.width ?? node.width ?? (compact ? 190 : 250);
      const height =
        node.measured?.height ??
        node.height ??
        (compact
          ? showBadges
            ? 72
            : 56
          : showBadges
            ? 92
            : 72);
      setCenter(node.position.x + width / 2, node.position.y + height / 2, {
        zoom: compact ? 1.15 : 1.05,
        duration: 320,
      });
    },
    [compact, showBadges, decorated.nodes, getNode, setCenter],
  );

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (!value.trim()) return;
      const matches = findMatchingIds(model.nodesById, value);
      if (!matches.size) return;
      commitExpanded((prev) => {
        const ensured = expandPathsToMatches(model.nodesById, matches);
        return new Set([...prev, ...ensured]);
      });
      window.setTimeout(() => fitView({ padding: 0.2, duration: 280 }), 80);
    },
    [commitExpanded, fitView, model.nodesById],
  );

  const selected = useMemo<SelectedClusterNode | null>(() => {
    if (!selectedId) return null;
    const node = model.nodesById.get(selectedId);
    if (!node) return null;
    const ancestorIds = getAncestorIds(model.nodesById, selectedId).reverse();
    const pathNodes = [...ancestorIds, selectedId]
      .map(id => {
        const item = model.nodesById.get(id);
        return item ? { id: item.id, label: item.label } : null;
      })
      .filter(
        (item): item is { id: string; label: string } => item !== null
      );
    return {
      ...node,
      pathNodes,
      childCount: node.childIds.length,
      children: node.childIds.map((id) => {
        const child = model.nodesById.get(id);
        if (!child) {
          throw new Error(`Child node "${id}" was not found in the tree model.`);
        }
        return {
          id,
          label: child.label,
          isLeaf: child.isLeaf,
          childCount: child.childIds.length,
          seo: child.seo,
        };
      }),
    };
  }, [model.nodesById, selectedId]);

  const pinnedNodes = useMemo(
    () =>
      [...pinnedIds]
        .map((id) => model.nodesById.get(id))
        .filter((node): node is TreeNode => node !== undefined)
        .map((node) => ({ id: node.id, label: node.label })),
    [model.nodesById, pinnedIds],
  );

  const onNodeClick = useCallback(
    (_event: ReactMouseEvent, node: FlowNode) => {
      setSelectedId(node.id);
      setContextMenu(null);
      commitExpanded((prev) => {
        const next = new Set(prev);
        getAncestorIds(model.nodesById, node.id).forEach((id) => next.add(id));
        return next;
      });
    },
    [commitExpanded, model.nodesById],
  );

  const openEditEditor = useCallback(
    (nodeId: string) => {
      if (!ensureEditable()) return;
      setSelectedId(nodeId);
      setEditorMode('edit');
      setEditorTargetId(nodeId);
      setEditorOpen(true);
    },
    [ensureEditable]
  );

  const pendingDeleteNode = pendingDeleteId
    ? model.nodesById.get(pendingDeleteId) ?? null
    : null;

  const deleteModalDesc = useMemo(() => {
    if (!pendingDeleteNode) return '';
    if (pendingDeleteNode.isLeaf) {
      return `آیا مطمئن هستید می‌خواهید کلمه کلیدی «${pendingDeleteNode.label}» را حذف کنید؟`;
    }
    const descendantCount = getDescendantIds(
      model.nodesById,
      pendingDeleteNode.id
    ).length;
    return `با حذف خوشه «${pendingDeleteNode.label}»، همه ${descendantCount} زیرمجموعه داخل آن هم حذف می‌شوند و این کار قابل بازگشت نیست. آیا ادامه می‌دهید؟`;
  }, [model.nodesById, pendingDeleteNode]);

  const confirmDeleteNode = useCallback(async () => {
    if (!pendingDeleteId || !hierarchical || !onHierarchicalChange) return;
    const treeNode = model.nodesById.get(pendingDeleteId);
    if (!treeNode) return;

    const path = getRawPathFromModel(model.nodesById, pendingDeleteId);
    const body = {
      task_id: taskId,
      path: toApiNamePath(path)
    };
    try {
      await deleteNode(body).unwrap();
      const next = deleteNodeAtPath(hierarchical, path);
      onHierarchicalChange(next);
      setSelectedId(treeNode.parentId);
      setPinnedIds(prev => {
        const nextPins = new Set(prev);
        nextPins.delete(pendingDeleteId);
        return nextPins;
      });
      setPendingDeleteId(null);
      successMessage('نود حذف شد');
      showStatus('نود حذف شد');
    } catch (err) {
      errorMessage(
        err instanceof Error ? err.message : 'حذف نود با خطا مواجه شد'
      );
      throw err;
    }
  }, [
    deleteNode,
    hierarchical,
    model.nodesById,
    onHierarchicalChange,
    pendingDeleteId,
    showStatus,
    taskId
  ]);

  const handleSubmitAdd = useCallback(
    async (payload: NewNodePayload) => {
      if (!hierarchical || !onHierarchicalChange || !editorTargetId) return;
      const parentPath = getRawPathFromModel(model.nodesById, editorTargetId);
      const body = {
        task_id: taskId,
        parent_path: toApiNamePath(parentPath),
        node: { name: payload.name.trim() },
        position: 'end' as const
      };
      const res = await addNode(body).unwrap();
      const child = hierarchicalNodeFromApi(res.data);
      const next = addChildNode(hierarchical, parentPath, child, 'end');
      onHierarchicalChange(next);
      commitExpanded(prev => {
        const nextExpanded = new Set(prev);
        nextExpanded.add(editorTargetId);
        getAncestorIds(model.nodesById, editorTargetId).forEach(id =>
          nextExpanded.add(id)
        );
        return nextExpanded;
      });
      setEditorOpen(false);
      successMessage('نود اضافه شد');
      showStatus('نود جدید اضافه شد');
    },
    [
      addNode,
      commitExpanded,
      editorTargetId,
      hierarchical,
      model.nodesById,
      onHierarchicalChange,
      showStatus,
      taskId
    ]
  );

  const handleSubmitEdit = useCallback(
    async (patch: NodePatch) => {
      if (!hierarchical || !onHierarchicalChange || !editorTargetId) return;
      const path = getRawPathFromModel(model.nodesById, editorTargetId);
      const body = {
        task_id: taskId,
        path: toApiNamePath(path),
        patch: { name: patch.name.trim() }
      };
      await updateNode(body).unwrap();
      const next = updateNodeAtPath(hierarchical, path, patch);
      onHierarchicalChange(next);
      setEditorOpen(false);
      successMessage('نود ویرایش شد');
      showStatus('نود به‌روزرسانی شد');
    },
    [
      editorTargetId,
      hierarchical,
      model.nodesById,
      onHierarchicalChange,
      showStatus,
      taskId,
      updateNode
    ]
  );

  const onNodeContextMenu = useCallback(
    (event: ReactMouseEvent, node: FlowNode) => {
      event.preventDefault();
      if (!nodesEditable || locked) return;
      setSelectedId(node.id);
      setContextMenu({
        x: event.clientX,
        y: event.clientY,
        nodeId: node.id,
        label: String(node.data?.label ?? ''),
        isLeaf: Boolean(node.data?.isLeaf),
        isRoot: (node.data?.depth ?? 0) === 0
      });
    },
    [locked, nodesEditable]
  );

  const onFocusChild = useCallback(
    (childId: string) => {
      const child = model.nodesById.get(childId);
      if (!child) return;
      commitExpanded((prev) => {
        const next = new Set(prev);
        getAncestorIds(model.nodesById, childId).forEach((id) => next.add(id));
        if (!child.isLeaf) next.add(childId);
        return next;
      });
      setSelectedId(childId);
      window.setTimeout(() => focusNode(childId), 80);
    },
    [commitExpanded, focusNode, model.nodesById],
  );

  const togglePin = useCallback(
    (id: string) => {
      if (locked) {
        showStatus('در حالت قفل امکان پین نیست');
        return;
      }
      setPinnedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [locked, showStatus],
  );

  const revealMiniMap = useCallback(() => {
    if (miniMapHideTimer.current) {
      window.clearTimeout(miniMapHideTimer.current);
      miniMapHideTimer.current = null;
    }
    setShowMiniMap(true);
  }, []);

  const scheduleHideMiniMap = useCallback(() => {
    if (miniMapHideTimer.current) {
      window.clearTimeout(miniMapHideTimer.current);
    }
    miniMapHideTimer.current = window.setTimeout(() => {
      setShowMiniMap(false);
      miniMapHideTimer.current = null;
    }, 900);
  }, []);

  useEffect(
    () => () => {
      if (miniMapHideTimer.current) {
        window.clearTimeout(miniMapHideTimer.current);
      }
    },
    [],
  );

  const handleCanvasZoomIn = useCallback(() => {
    revealMiniMap();
    zoomIn({ duration: 180 });
    scheduleHideMiniMap();
  }, [revealMiniMap, scheduleHideMiniMap, zoomIn]);

  const handleCanvasZoomOut = useCallback(() => {
    revealMiniMap();
    zoomOut({ duration: 180 });
    scheduleHideMiniMap();
  }, [revealMiniMap, scheduleHideMiniMap, zoomOut]);

  const handleCanvasFitView = useCallback(() => {
    revealMiniMap();
    fitView({ padding: 0.18, duration: 220 });
    scheduleHideMiniMap();
  }, [fitView, revealMiniMap, scheduleHideMiniMap]);

  const prepareFullTree = useCallback(async (): Promise<void> => {
    commitExpanded(new Set(collectExpandableIds(model.nodesById)));
    await new Promise<void>((resolve) => window.setTimeout(resolve, 240));
    fitView({ padding: 0.18, duration: 0 });
    await new Promise<void>((resolve) => window.setTimeout(resolve, 140));
  }, [commitExpanded, fitView, model.nodesById]);

  const handleExport = useCallback(
    async (
      type:
        | 'preview-png'
        | 'png'
        | 'jpeg'
        | 'svg'
        | 'json'
        | 'csv'
        | 'txt'
        | 'copy-keywords'
        | 'copy-path'
    ) => {
      if (!clusterData || exporting) return;
      const base = getExportBaseName(clusterData);
      try {
        setExporting(true);

        if (type === 'preview-png' || type === 'png' || type === 'jpeg' || type === 'svg') {
          await prepareFullTree();

          if (type === 'preview-png') {
            setPreview({ show: true, loading: true, url: '', filename: `${base}-tree.png` });
            const dataUrl = await captureFlowPng({ getNodes });
            setPreview({
              show: true,
              loading: false,
              url: dataUrl,
              filename: `${base}-tree.png`
            });
            showStatus('پیش‌نمایش آماده شد');
            return;
          }
          if (type === 'png') {
            await exportFlowPng({ getNodes, filename: `${base}-tree.png` });
            showStatus('تصویر PNG دانلود شد');
            return;
          }
          if (type === 'jpeg') {
            await exportFlowJpeg({ getNodes, filename: `${base}-tree.jpg` });
            showStatus('تصویر JPEG دانلود شد');
            return;
          }
          await exportFlowSvg({ getNodes, filename: `${base}-tree.svg` });
          showStatus('فایل SVG دانلود شد');
          return;
        }

        if (type === 'json') {
          exportDecodedJson(clusterData);
          showStatus('JSON دانلود شد');
          return;
        }
        if (type === 'csv') {
          exportPathsCsv(clusterData);
          showStatus('CSV مسیرها دانلود شد');
          return;
        }
        if (type === 'txt') {
          exportKeywordsTxt(clusterData);
          showStatus('لیست کلمات دانلود شد');
          return;
        }
        if (type === 'copy-keywords') {
          const count = await copyKeywords(clusterData);
          showStatus(`${count} کلمه کپی شد`);
          return;
        }
        if (type === 'copy-path') {
          if (!selected?.path?.length) {
            showStatus('ابتدا یک نود را انتخاب کنید');
            return;
          }
          await copySelectedPath(selected.path);
          showStatus('مسیر کپی شد');
        }
      } catch (error) {
        console.error(error);
        showStatus(
          error instanceof Error ? error.message : 'خروجی با خطا مواجه شد'
        );
        setPreview((prev) => ({ ...prev, loading: false }));
      } finally {
        setExporting(false);
      }
    },
    [clusterData, exporting, getNodes, prepareFullTree, selected, showStatus],
  );

  const onUndo = useCallback(() => {
    setUndoStack((stack) => {
      if (!stack.length) return stack;
      const previous = stack[stack.length - 1];
      setExpandedIds(new Set(previous));
      return stack.slice(0, -1);
    });
  }, []);

  const fullscreenTarget = useCallback(
    () => fullscreenRootRef?.current ?? workspaceRef.current,
    [fullscreenRootRef]
  );

  const toggleFullscreen = useCallback(async () => {
    const el = fullscreenTarget();
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch {
      setFullscreen(prev => !prev);
    }
  }, [fullscreenTarget]);

  useEffect(() => {
    const el = fullscreenTarget();
    if (!el) return;
    const onPage = el !== workspaceRef.current;
    el.classList.toggle('cluster-page--fullscreen', fullscreen && onPage);
    el.classList.toggle('cluster-workspace--fullscreen', fullscreen && !onPage);
    el.classList.toggle('cluster-workspace--faux-full', fullscreen && !onPage && !document.fullscreenElement);
    return () => {
      el.classList.remove('cluster-page--fullscreen');
      el.classList.remove('cluster-workspace--fullscreen');
      el.classList.remove('cluster-workspace--faux-full');
    };
  }, [fullscreen, fullscreenTarget]);

  useEffect(() => {
    const onFsChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      if (document.fullscreenElement === fullscreenTarget()) {
        void document.exitFullscreen();
      }
    };
  }, [fullscreenTarget]);

  const applyPresentationStep = useCallback(
    (step: number) => {
      setPresentationStep(step);
      commitExpanded(defaultExpandedIds(model.nodesById, step));
      window.setTimeout(() => fitView({ padding: 0.2, duration: 280 }), 80);
    },
    [commitExpanded, fitView, model.nodesById],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;

      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomIn({ duration: 180 });
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomOut({ duration: 180 });
      } else if (event.key === '0') {
        event.preventDefault();
        fitView({ padding: 0.18, duration: 280 });
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (!locked) onUndo();
      } else if (event.key === 'f' || event.key === 'F') {
        event.preventDefault();
        toggleFullscreen();
      } else if (event.key === 'l' || event.key === 'L') {
        event.preventDefault();
        setLocked((prev) => {
          const next = !prev;
          showStatus(next ? 'حالت قفل فعال شد' : 'قفل برداشته شد');
          return next;
        });
      } else if (event.key === 'ArrowRight' && presentation && !locked) {
        event.preventDefault();
        applyPresentationStep(Math.min(stats.maxDepth, presentationStep + 1));
      } else if (event.key === 'ArrowLeft' && presentation && !locked) {
        event.preventDefault();
        applyPresentationStep(Math.max(0, presentationStep - 1));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    applyPresentationStep,
    fitView,
    locked,
    onUndo,
    presentation,
    presentationStep,
    showStatus,
    stats.maxDepth,
    toggleFullscreen,
    zoomIn,
    zoomOut,
  ]);

  return (
    <div
      ref={workspaceRef}
      className={`cluster-workspace d-flex flex-column gap-3 min-h-0${
        locked ? ' cluster-workspace--locked' : ''
      }`}
    >
      <ClusterToolbar
        query={query}
        onQueryChange={handleQueryChange}
        matchCount={matchIds.size}
        onExpandAll={() => {
          if (locked) return;
          commitExpanded(new Set(collectExpandableIds(model.nodesById)));
          window.setTimeout(() => fitView({ padding: 0.18, duration: 280 }), 80);
        }}
        onCollapseAll={() => {
          if (locked) return;
          commitExpanded(defaultExpandedIds(model.nodesById, 0));
          window.setTimeout(() => fitView({ padding: 0.18, duration: 280 }), 80);
        }}
        onFitView={() => fitView({ padding: 0.18, duration: 280 })}
        onClearSelection={() => setSelectedId(null)}
        hasSelection={Boolean(selectedId)}
        statusMessage={statusMessage}
        depthFilter={depthFilter}
        onDepthFilterChange={(value) => {
          if (locked) return;
          setDepthFilter(value);
          if (value != null) {
            commitExpanded(defaultExpandedIds(model.nodesById, Math.max(0, value - 1)));
          }
        }}
        maxDepth={stats.maxDepth}
        compact={compact}
        onToggleCompact={() => setCompact((v) => !v)}
        showBadges={showBadges}
        onToggleBadges={() => setShowBadges((v) => !v)}
        onUndo={() => {
          if (locked) return;
          onUndo();
        }}
        canUndo={undoStack.length > 0}
        onResetLayout={() => {
          if (locked) return;
          setLayoutKey((k) => k + 1);
          window.setTimeout(() => fitView({ padding: 0.18, duration: 280 }), 60);
          showStatus('جایگاه نودها ریست شد');
        }}
        onOpenCompare={() => setCompareOpen(true)}
        presentation={presentation}
        onTogglePresentation={() => {
          if (locked) return;
          setPresentation((prev) => {
            const next = !prev;
            if (next) {
              setPresentationStep(0);
              commitExpanded(defaultExpandedIds(model.nodesById, 0));
              showStatus('حالت ارائه فعال شد');
            } else {
              showStatus('حالت ارائه خاموش شد');
            }
            return next;
          });
        }}
        onPresentationPrev={() => {
          if (locked) return;
          applyPresentationStep(Math.max(0, presentationStep - 1));
        }}
        onPresentationNext={() => {
          if (locked) return;
          applyPresentationStep(Math.min(stats.maxDepth, presentationStep + 1));
        }}
        presentationStep={presentationStep}
        presentationMax={stats.maxDepth}
        locked={locked}
        onToggleLock={() => {
          setLocked((prev) => {
            const next = !prev;
            showStatus(next ? 'حالت قفل فعال شد' : 'قفل برداشته شد');
            return next;
          });
        }}
        clusterMode={clusterMode}
        onClusterModeChange={(mode) => {
          onClusterModeChange?.(mode);
        }}
        clusterSummary={clusterSummary}
        thresholds={thresholds}
      />

      <ClusterBreadcrumb
        pathIds={[...pathIds]}
        nodesById={model.nodesById}
        onNavigate={(id) => {
          setSelectedId(id);
          focusNode(id);
        }}
      />

      <ClusterStats stats={stats} pinnedCount={pinnedIds.size} />

      <Row className="g-3 cluster-workspace__body" dir="rtl">
        <Col lg={3} className="cluster-workspace__aside min-h-0">
          <Card className="h-100 overflow-hidden mb-0">
            <Card.Body className="p-0 h-100">
              <ClusterDetails
                selected={selected}
                onFocusChild={onFocusChild}
                onNavigatePath={onFocusChild}
                onTogglePin={togglePin}
                isPinned={selectedId ? pinnedIds.has(selectedId) : false}
                pinnedNodes={pinnedNodes}
                locked={locked}
                canEditNodes={nodesEditable}
                editBusy={editBusy}
                onAddChild={openAddEditor}
                onEditNode={openEditEditor}
                onDeleteNode={requestDeleteNode}
                onFocusPinned={(id) => {
                  setSelectedId(id);
                  focusNode(id);
                }}
                onUnpin={(id) => {
                  if (locked) return;
                  setPinnedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                  });
                }}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={9} className="cluster-workspace__canvas min-h-0">
          <Card className="h-100 overflow-hidden mb-0">
            <Card.Body className="p-0 h-100">
              <div className="cluster-flow h-100">
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={onNodeClick}
                  onNodeContextMenu={onNodeContextMenu}
                  onPaneClick={() => {
                    setSelectedId(null);
                    setContextMenu(null);
                  }}
                  onMoveStart={(event) => {
                    if (event) revealMiniMap();
                  }}
                  onMoveEnd={scheduleHideMiniMap}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.18 }}
                  minZoom={0.1}
                  maxZoom={2}
                  nodesDraggable={!locked}
                  nodesConnectable={false}
                  elementsSelectable
                  zoomOnScroll
                  panOnScroll={false}
                  nodesFocusable={!locked}
                  proOptions={{ hideAttribution: true }}
                >
                  <ClusterCanvasControls
                    onZoomIn={handleCanvasZoomIn}
                    onZoomOut={handleCanvasZoomOut}
                    onFitView={handleCanvasFitView}
                    fullscreen={fullscreen}
                    onToggleFullscreen={toggleFullscreen}
                    exporting={exporting}
                    onExport={handleExport}
                    hasSelection={Boolean(selectedId)}
                  />
                  {showMiniMap ? (
                    <MiniMap
                      pannable
                      zoomable
                      position="bottom-right"
                      nodeStrokeColor={node =>
                        node.selected
                          ? '#2c7be5'
                          : node.data?.isLeaf
                            ? '#00d27a'
                            : '#748194'
                      }
                      nodeColor={node =>
                        node.data?.isPinned
                          ? '#fd7e14'
                          : node.data?.isMatch
                            ? '#f5803e'
                            : node.data?.isLeaf
                              ? isDark
                                ? '#1b4332'
                                : '#d1e7dd'
                              : isDark
                                ? '#1b3a5a'
                                : '#cfe2ff'
                      }
                      maskColor={
                        isDark
                          ? 'rgba(0, 0, 0, 0.45)'
                          : 'rgba(15, 23, 42, 0.08)'
                      }
                    />
                  ) : null}
                  <Panel position="top-right">
                    <ClusterBadgeLegend showBadges={showBadges} />
                  </Panel>
                </ReactFlow>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ExportPreviewModal
        show={preview.show}
        onHide={() => setPreview((prev) => ({ ...prev, show: false }))}
        previewUrl={preview.url}
        loading={preview.loading}
        filename={preview.filename}
        format="png"
      />

      <CompareModal
        show={compareOpen}
        onHide={() => setCompareOpen(false)}
        primaryData={clusterData}
      />

      <ClusterNodeContextMenu
        menu={contextMenu}
        locked={locked}
        canEdit={nodesEditable}
        onClose={() => setContextMenu(null)}
        onAddChild={openAddEditor}
        onEdit={openEditEditor}
        onDelete={requestDeleteNode}
      />

      <ClusterNodeEditorModal
        show={editorOpen}
        mode={editorMode}
        busy={editBusy}
        parentLabel={
          editorMode === 'add' && editorTargetId
            ? model.nodesById.get(editorTargetId)?.label
            : undefined
        }
        node={
          editorMode === 'edit' && editorTargetId && selected?.id === editorTargetId
            ? selected
            : editorMode === 'edit' && editorTargetId
              ? (() => {
                  const item = model.nodesById.get(editorTargetId);
                  if (!item) return null;
                  return {
                    ...item,
                    pathNodes: [],
                    childCount: item.childIds.length,
                    children: []
                  };
                })()
              : null
        }
        onHide={() => setEditorOpen(false)}
        onSubmitAdd={handleSubmitAdd}
        onSubmitEdit={handleSubmitEdit}
      />

      <CancelModal
        show={showDeleteModal}
        setShow={(value: boolean) => {
          setShowDeleteModal(value);
          if (!value) setPendingDeleteId(null);
        }}
        title={
          pendingDeleteNode?.isLeaf ? 'حذف کلمه کلیدی' : 'حذف خوشه'
        }
        desc={deleteModalDesc}
        submitTitle="آره، حذف بشه"
        submitFunc={confirmDeleteNode}
        isSubmitting={isDeleting}
        buttonSize="sm"
      />
    </div>
  );
}

export default function ClusterFlow({
  hierarchical,
  clusterData,
  clusterMode,
  onClusterModeChange,
  clusterSummary,
  thresholds,
  onHierarchicalChange,
  canEditNodes,
  fullscreenRootRef
}: ClusterFlowProps) {
  return (
    <ReactFlowProvider>
      <ClusterFlowCanvas
        hierarchical={hierarchical}
        clusterData={clusterData}
        clusterMode={clusterMode}
        onClusterModeChange={onClusterModeChange}
        clusterSummary={clusterSummary}
        thresholds={thresholds}
        onHierarchicalChange={onHierarchicalChange}
        canEditNodes={canEditNodes}
        fullscreenRootRef={fullscreenRootRef}
      />
    </ReactFlowProvider>
  );
}
